/**
 * @description Hook to refetch the notifications when the location changes
 * @note Use this hook at the app level (App.tsx) for global unread count tracking
 * @note Does NOT refetch on /chat routes because Pusher handles real-time updates
 * @returns {void}
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATIONS_STALE_TIME,
} from "@/features/notifications/constants/notifications.constants";
import { notificationsInvalidationService } from "@/features/notifications/services/notifications-invalidation.service";

export const useNotificationsRefetch = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const pathname = location.pathname;
  const isFirstRun = useRef(true);

  useEffect(() => {
    // The NotificationsProvider query already fetches on mount; skip the first
    // run so navigation tracking doesn't stack a second fetch at boot.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    if (pathname.startsWith("/chat")) {
      return;
    }

    // Respect the query staleTime: a forced invalidate refetches immediately
    // (active observer), so only refresh when the cached count is stale —
    // otherwise repeated same-window navigations each trigger a useless fetch.
    const state = queryClient.getQueryState(NOTIFICATIONS_QUERY_KEY);
    const isFresh = !!state?.dataUpdatedAt
      && Date.now() - state.dataUpdatedAt < NOTIFICATIONS_STALE_TIME;
    if (isFresh) {
      return;
    }

    notificationsInvalidationService.invalidateNotifications(queryClient);
  }, [pathname, queryClient]);
};
